import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  UploadCloud, FileDown, Loader2, CheckCircle2, AlertTriangle, FileSpreadsheet, ArrowRight, History,
} from "lucide-react";
import { toast } from "sonner";
import { PriorGradesImport } from "@/components/PriorGradesImport";

const STEPS = ["Seleção", "Upload", "Pré-visualização", "Concluído"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Importacao() {
  const { scopeParams, selectedSchoolId } = useAuth();
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classId, setClassId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [term, setTerm] = useState("1");
  const [step, setStep] = useState(0);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("notas");
  const navigate = useNavigate();

  const fetchHistory = useCallback(() => api.get("/imports").then((r) => setHistory(r.data)).catch(() => {}), []);
  useEffect(() => {
    api.get("/classes", { params: scopeParams }).then((r) => setClasses(r.data));
    api.get("/subjects", { params: scopeParams }).then((r) => setSubjects(r.data));
    fetchHistory();
  }, [scopeParams, selectedSchoolId, fetchHistory]);

  const ready = classId && subjectId && term;

  const downloadTemplate = async (format) => {
    try {
      const { data } = await api.get("/imports/template", {
        params: { class_id: classId, subject_id: subjectId, term_order: Number(term), format },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `modelo_notas_${term}tri.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("class_id", classId);
      fd.append("subject_id", subjectId);
      fd.append("term_order", term);
      const { data } = await api.post("/imports/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setStep(2);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/imports/${preview.id}/confirm`);
      setResult(data);
      setStep(3);
      fetchHistory();
      toast.success("Importação concluída");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const reset = () => { setStep(0); setPreview(null); setResult(null); };

  const className = classes.find((c) => c.id === classId)?.name;
  const subjectName = subjects.find((s) => s.id === subjectId)?.name;

  return (
    <div>
      <PageHeader title="Importação" subtitle="Importe por turma/disciplina (notas) ou planilha completa de turmas (notas + faltas)" />
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-5 h-auto max-w-full flex-wrap justify-start" data-testid="import-tabs">
          <TabsTrigger value="notas" data-testid="import-tab-notas">Notas (por turma)</TabsTrigger>
          <TabsTrigger value="turmas" data-testid="import-tab-turmas">Turmas (planilha completa)</TabsTrigger>
          <TabsTrigger value="anteriores" data-testid="import-tab-anteriores">Notas anteriores</TabsTrigger>
        </TabsList>
        <TabsContent value="notas">

      {/* Stepper */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${i === step ? "bg-blue-700 text-white" : i < step ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-400"}`}>
              <span className="grid h-5 w-5 place-items-center rounded-full bg-white/25 text-[10px]">{i + 1}</span>{s}
            </div>
            {i < STEPS.length - 1 && <ArrowRight className="h-4 w-4 text-slate-300" />}
          </React.Fragment>
        ))}
      </div>

      {step === 0 && (
        <Card className="max-w-2xl border-slate-200 p-6">
          <h3 className="mb-4 font-display font-semibold text-slate-800">1. Selecione a turma, disciplina e trimestre</h3>
          <div className="grid gap-4 sm:grid-cols-3">
            <div><Label>Turma</Label>
              <Select value={classId} onValueChange={setClassId}>
                <SelectTrigger data-testid="import-class-select"><SelectValue placeholder="Turma" /></SelectTrigger>
                <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.grade}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Disciplina</Label>
              <Select value={subjectId} onValueChange={setSubjectId}>
                <SelectTrigger data-testid="import-subject-select"><SelectValue placeholder="Disciplina" /></SelectTrigger>
                <SelectContent>{subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Trimestre</Label>
              <Select value={term} onValueChange={setTerm}>
                <SelectTrigger data-testid="import-term-select"><SelectValue /></SelectTrigger>
                <SelectContent>{[1, 2, 3].map((t) => <SelectItem key={t} value={String(t)}>{t}º Trimestre</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button variant="outline" disabled={!ready} onClick={() => downloadTemplate("xlsx")} data-testid="import-template-xlsx"><FileDown className="mr-1.5 h-4 w-4" /> Modelo XLSX</Button>
            <Button variant="outline" disabled={!ready} onClick={() => downloadTemplate("csv")} data-testid="import-template-csv"><FileDown className="mr-1.5 h-4 w-4" /> Modelo CSV</Button>
            <Button disabled={!ready} onClick={() => setStep(1)} data-testid="import-next-button" className="bg-blue-700 hover:bg-blue-800">Avançar <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
          </div>
          <p className="mt-3 text-xs text-slate-400">O modelo já vem com Matrícula e Nome preenchidos. Basta inserir as notas nas colunas Trabalho (0 a 2), Teste (0 a 3) e Prova (0 a 5). Para a NOA, adicione a coluna "NOA" (0 a 10): ela só vale para quem tiver nota do trimestre menor que 5,0 e substitui essa nota.</p>
        </Card>
      )}

      {step === 1 && (
        <Card className="max-w-2xl border-slate-200 p-6">
          <h3 className="mb-1 font-display font-semibold text-slate-800">2. Envie a planilha preenchida</h3>
          <p className="mb-4 text-sm text-slate-500">{className} · {subjectName} · {term}º trimestre</p>
          <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 py-12 transition hover:border-blue-400 hover:bg-blue-50">
            {busy ? <Loader2 className="h-10 w-10 animate-spin text-blue-700" /> : <UploadCloud className="h-10 w-10 text-slate-400" />}
            <span className="mt-2 text-sm font-semibold text-slate-600">Clique para selecionar o arquivo</span>
            <span className="text-xs text-slate-400">XLSX ou CSV</span>
            <input type="file" accept=".xlsx,.csv" className="hidden" onChange={onFile} data-testid="import-file-input" disabled={busy} />
          </label>
          <Button variant="ghost" onClick={() => setStep(0)} className="mt-4">Voltar</Button>
        </Card>
      )}

      {step === 2 && preview && (
        <div>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Linhas" value={preview.summary.total} />
            <Stat label="Prontas" value={preview.summary.ok} cls="text-green-700" />
            <Stat label="Pendências" value={preview.summary.pendencias} cls="text-red-600" />
            <Stat label="Novas avaliações" value={preview.summary.novas_avaliacoes} cls="text-orange-600" />
          </div>
          <div className="mb-3 flex flex-wrap gap-2">
            {preview.columns.map((col) => (
              <span key={col.header} className={`rounded-full px-2.5 py-1 text-xs font-semibold ${col.status === "nova" ? "bg-orange-100 text-orange-700" : "bg-blue-50 text-blue-700"}`}>
                {col.header} · {col.status === "nova" ? "nova avaliação" : "existente"}
              </span>
            ))}
          </div>
          <Card className="overflow-x-auto border-slate-200" data-testid="import-preview-table">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Matrícula</TableHead><TableHead>Estudante</TableHead>
                {preview.columns.map((col) => <TableHead key={col.header} className="text-center">{col.header}</TableHead>)}
                <TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {preview.rows.map((r, i) => (
                  <TableRow key={i} data-testid={`import-row-${i}`} className={r.status === "PENDENCIA" ? "bg-red-50/40" : ""}>
                    <TableCell className="font-mono text-xs">{r.matricula}</TableCell>
                    <TableCell className="font-medium text-slate-700">{r.student_name}</TableCell>
                    {preview.columns.map((col) => <TableCell key={col.header} className="text-center">{r.values[col.header] ?? "—"}</TableCell>)}
                    <TableCell>
                      {r.status === "OK"
                        ? <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="mr-1 h-3 w-3" /> Pronto</Badge>
                        : <Badge className="bg-red-100 text-red-700" title={r.issues.join(", ")}><AlertTriangle className="mr-1 h-3 w-3" /> Pendência</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          {preview.summary.pendencias > 0 && (
            <p className="mt-2 text-xs text-amber-600">Linhas em pendência (matrícula não encontrada) NÃO serão importadas. Nenhum estudante é criado automaticamente.</p>
          )}
          <div className="mt-4 flex gap-2">
            <Button variant="outline" onClick={() => setStep(1)}>Voltar</Button>
            <Button onClick={confirm} disabled={busy || preview.summary.ok === 0} data-testid="import-confirm-button" className="bg-blue-700 hover:bg-blue-800">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Confirmar Importação (${preview.summary.ok})`}
            </Button>
          </div>
        </div>
      )}

      {step === 3 && result && (
        <Card className="max-w-3xl border-slate-200 p-6">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-10 w-10 text-green-600" />
            <div><h3 className="font-display text-xl font-bold text-slate-900">Importação concluída</h3>
              <p className="text-sm text-slate-500">{result.grades_applied} notas aplicadas · {result.created_assessments} avaliações criadas · {result.pendencias} pendências</p></div>
          </div>
          <h4 className="mt-5 mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Consolidação do trimestre</h4>
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <Table>
              <TableHeader><TableRow><TableHead>Estudante</TableHead><TableHead className="text-center">Média Final</TableHead><TableHead>Situação</TableHead></TableRow></TableHeader>
              <TableBody>
                {result.consolidation.map((r) => (
                  <TableRow key={r.student_id}>
                    <TableCell className="font-medium text-slate-700">{r.full_name}</TableCell>
                    <TableCell className="text-center font-bold">{r.final ?? "—"}</TableCell>
                    <TableCell>{r.situation === "SATISFATORIO" ? <Badge className="bg-green-100 text-green-700">Satisfatório</Badge> : r.situation === "ABAIXO" ? <Badge className="bg-red-100 text-red-700">Abaixo</Badge> : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="mt-5 flex gap-2">
            <Button onClick={reset} variant="outline">Nova importação</Button>
            <Button onClick={() => navigate(`/diario?class_id=${preview.class_id}&subject_id=${preview.subject_id}&cn=${encodeURIComponent(className || "")}&sn=${encodeURIComponent(subjectName || "")}`)} className="bg-blue-700 hover:bg-blue-800">Abrir Diário</Button>
          </div>
        </Card>
      )}

      {/* History */}
      <div className="mt-8">
        <h2 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400"><History className="h-4 w-4" /> Histórico de importações</h2>
        {history.length === 0 ? (
          <EmptyState icon={FileSpreadsheet} title="Nenhuma importação ainda" />
        ) : (
          <Card className="overflow-x-auto border-slate-200">
            <Table>
              <TableHeader><TableRow><TableHead>Arquivo</TableHead><TableHead>Trimestre</TableHead><TableHead className="text-center">Linhas</TableHead><TableHead className="text-center">OK</TableHead><TableHead className="text-center">Pend.</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {history.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell className="font-medium text-slate-700">{h.filename}</TableCell>
                    <TableCell>{h.term_order}º</TableCell>
                    <TableCell className="text-center">{h.summary?.total}</TableCell>
                    <TableCell className="text-center text-green-700">{h.summary?.ok}</TableCell>
                    <TableCell className="text-center text-red-600">{h.summary?.pendencias}</TableCell>
                    <TableCell>{h.status === "CONCLUIDA" ? <Badge className="bg-green-100 text-green-700">Concluída</Badge> : <Badge variant="outline">Rascunho</Badge>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
        </TabsContent>
        <TabsContent value="turmas">
          <TurmasImport scopeParams={scopeParams} selectedSchoolId={selectedSchoolId} />
        </TabsContent>
        <TabsContent value="anteriores">
          <PriorGradesImport scopeParams={scopeParams} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TurmasImport({ scopeParams }) {
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const download = async (format) => {
    try {
      const { data } = await api.get("/imports/turmas/template", { params: { format }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a"); a.href = url; a.download = `modelo_turmas.${format}`; a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      if (scopeParams.school_id) fd.append("school_id", scopeParams.school_id);
      const { data } = await api.post("/imports/turmas/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data); setResult(null);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const confirm = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/imports/turmas/${preview.id}/confirm`); setResult(data); setPreview(null); toast.success("Importação concluída"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const BADGE = { OK: "bg-green-100 text-green-700", ERRO: "bg-red-100 text-red-700", IGNORADO: "bg-slate-100 text-slate-500" };
  return (
    <div className="space-y-4">
      <Card className="border-slate-200 p-5">
        <h3 className="mb-2 font-display font-semibold text-slate-800">Planilha de turmas (notas + faltas)</h3>
        <p className="mb-4 text-sm text-slate-500">Colunas: Matrícula (até 20 dígitos), Nome do aluno, Turma, Disciplina, Trimestre, Trabalho (0 a 2), Teste (0 a 3), Prova (0 a 5), Faltas. Turma e disciplina casadas pelo nome cadastrado. Alunos TRANSFERIDO/INATIVO são ignorados; a nota do trimestre é a soma Trabalho + Teste + Prova e as faltas ficam no perfil do aluno.</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => download("xlsx")} data-testid="turmas-template-xlsx"><FileDown className="mr-1.5 h-4 w-4" /> Modelo XLSX</Button>
          <Button variant="outline" onClick={() => download("csv")} data-testid="turmas-template-csv"><FileDown className="mr-1.5 h-4 w-4" /> Modelo CSV</Button>
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-blue-700 px-3 py-2 text-sm font-medium text-white hover:bg-blue-800" data-testid="turmas-upload-label">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Enviar planilha
            <input type="file" accept=".xlsx,.csv" className="hidden" onChange={onFile} disabled={busy} data-testid="turmas-file-input" />
          </label>
        </div>
      </Card>

      {preview && (
        <div>
          <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Linhas" value={preview.summary.total} />
            <Stat label="Prontas" value={preview.summary.ok} cls="text-green-700" />
            <Stat label="Ignorados" value={preview.summary.ignorados} cls="text-slate-500" />
            <Stat label="Erros" value={preview.summary.erros} cls="text-red-600" />
          </div>
          <Card className="overflow-x-auto border-slate-200" data-testid="turmas-preview-table">
            <Table>
              <TableHeader><TableRow><TableHead>Matrícula</TableHead><TableHead>Aluno</TableHead><TableHead>Turma</TableHead><TableHead>Disciplina</TableHead><TableHead className="text-center">Tri</TableHead><TableHead className="text-center">Trab.</TableHead><TableHead className="text-center">Teste</TableHead><TableHead className="text-center">Prova</TableHead><TableHead className="text-center">Faltas</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {preview.rows.map((r, i) => (
                  <TableRow key={i} data-testid={`turmas-row-${i}`}>
                    <TableCell className="font-mono text-xs">{r.matricula}</TableCell>
                    <TableCell className="text-sm text-slate-700">{r.nome}</TableCell>
                    <TableCell className="text-sm">{r.turma}</TableCell><TableCell className="text-sm">{r.disciplina}</TableCell>
                    <TableCell className="text-center">{r.trimestre ?? "—"}</TableCell>
                    {["TRABALHO", "TESTE", "PROVA"].map((k) => <TableCell key={k} className="text-center">{r.notas?.[k] ?? "—"}</TableCell>)}
                    <TableCell className="text-center">{r.faltas ?? "—"}</TableCell>
                    <TableCell><Badge className={BADGE[r.status]} title={(r.issues || []).join(", ")}>{r.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Button onClick={confirm} disabled={busy || preview.summary.ok === 0} data-testid="turmas-confirm-button" className="mt-4 bg-blue-700 hover:bg-blue-800">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Confirmar importação (${preview.summary.ok})`}</Button>
          {(preview.summary.erros + preview.summary.ignorados) > 0 && (
            <Button variant="outline" className="ml-2 mt-4" data-testid="turmas-errors-download" onClick={async () => {
              try { const { data } = await api.get(`/imports/turmas/${preview.id}/errors`, { params: { format: "xlsx" }, responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([data])); const a = document.createElement("a"); a.href = url; a.download = "turmas_erros.xlsx"; a.click(); window.URL.revokeObjectURL(url);
              } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }}>
              <FileDown className="mr-1.5 h-4 w-4" /> Baixar erros
            </Button>
          )}
        </div>
      )}

      {result && (
        <Card className="border-slate-200 p-6" data-testid="turmas-result">
          <div className="flex items-center gap-3"><CheckCircle2 className="h-9 w-9 text-green-600" />
            <div><h3 className="font-display text-lg font-bold text-slate-900">Importação concluída</h3>
              <p className="text-sm text-slate-500">{result.notas_aplicadas} nota(s) lançada(s) · {result.faltas_registradas} falta(s) registrada(s) · {result.ignorados} ignorado(s) · {result.erros} com erro</p></div>
          </div>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value, cls = "text-slate-900" }) {
  return <Card className="border-slate-200 p-4"><div className={`font-display text-3xl font-black ${cls}`}>{value}</div><div className="text-xs font-medium text-slate-500">{label}</div></Card>;
}
